import { GridColumnOptions } from '@/components/Grid/Classes';
import type MatrixRow from './MatrixRow';
import DateComp from '@/components/Basic/DateComp.vue';
import ValidatedInt from '@/components/Basic/ValidatedInt.vue';
import ValidatedText from '@/components/Basic/ValidatedText.vue';
import type { ExitStatus } from '@/objects/generated/enums/ExitStatus';
import MatrixDnxRenderer from '../SubComponents/MatrixDnxRenderer.vue';
import { propertyof } from '@/utilities';

const RowNextStartMarkerClass = 'grid-cell-warning';
const RowHasGPIMarkerClass = 'grid-cell-success';

let matrixGeneralColumnsCache: GridColumnOptions<MatrixRow>[] | null = null;

export const getMatrixGeneralColumns = (): GridColumnOptions<MatrixRow>[] => {
    if (matrixGeneralColumnsCache) {
        return matrixGeneralColumnsCache;
    }

    matrixGeneralColumnsCache = [
        new GridColumnOptions<MatrixRow, number>({
            dataField: propertyof<MatrixRow>('Bib'),
            columnHeaderText: '#',
            maxWidth: 70,
            minWidth: 70,
            resizable: false,
            pinned: 'left',
            cellRenderer: ValidatedInt,
        }),
        new GridColumnOptions<MatrixRow, string>({
            dataField: propertyof<MatrixRow>('ContestantName'),
            pinned: 'left',
            columnHeaderText: 'general.name',
            cellRenderer: ValidatedText,
        }),
        new GridColumnOptions<MatrixRow, Date | null>({
            dataField: propertyof<MatrixRow>('StartTime'),
            pinned: 'left',
            columnHeaderText: 'general.starttime',
            cellRenderer: DateComp,
            cellRendererParams: { format: 'TIME' },
            // TODO: Consider moving this stuff to MatrixStartGpiWarningHandler.ts
            cellClassRules: [
                {
                    className: RowNextStartMarkerClass,
                    func: (_, row) => {
                        if (!row.MatrixConfig.ShowGpiStartWarning) {
                            return false;
                        }

                        // GPI should have priority over start
                        if (row.HasStartGPI || row.HasStartPass) {
                            return false;
                        }

                        return row.IsNextToStart;
                    },
                },
                {
                    className: RowHasGPIMarkerClass,
                    func: (_, row) => {
                        if (!row.MatrixConfig.ShowGpiStartWarning) {
                            return false;
                        }

                        // If we have marked this as GPI then we have started
                        if (row.HasStartGPI) {
                            return true;
                        }

                        // A time on a station marked as start also counts as a start marker
                        if (row.HasStartPass) {
                            return true;
                        }

                        return false;
                    },
                },
            ],
            cellRefreshTrigger: (row, _) => {
                if (!row) {
                    return '';
                }

                return {
                    showGPI: row.MatrixConfig.ShowGpiStartWarning,
                    isNext: row.IsNextToStart,
                    hasGpi: row.HasStartGPI,
                    hasStartPass: row.HasStartPass,
                };
            },
        }),
        new GridColumnOptions<MatrixRow, ExitStatus>({
            dataField: propertyof<MatrixRow>('ExitStatus'),
            pinned: 'left',
            columnHeaderText: 'DNX',
            cellRenderer: MatrixDnxRenderer,
        }),
        new GridColumnOptions<MatrixRow, string>({
            dataField: propertyof<MatrixRow>('TeamName'),
            pinned: 'left',
            columnHeaderText: 'general.team',
            cellRenderer: ValidatedText,
        }),
        new GridColumnOptions<MatrixRow, string>({
            dataField: propertyof<MatrixRow>('GroupName'),
            pinned: 'left',
            columnHeaderText: 'general.group',
            cellRenderer: ValidatedText,
        }),
        new GridColumnOptions<MatrixRow, string>({
            dataField: propertyof<MatrixRow>('CategoryName'),
            pinned: 'left',
            columnHeaderText: 'general.category',
            cellRenderer: ValidatedText,
        }),
    ];

    return matrixGeneralColumnsCache;
};
